// components/FuturePlansStep.tsx
import React from 'react';
import { Control, Controller, FieldErrors, useWatch } from 'react-hook-form';
import { Row, Col, Typography, Divider, Select, Input } from 'antd';
import { FormLabel } from '@/components/Form';
import RadioGroupField from '@/components/Form/RadioGroupField';
import { ErrorText } from '@/components/Elements';
import { MONTHS, FUTURE_YEARS } from '@/constant/options';
import { PreLodgementChecklistFormValues } from '../types/preLodgementChecklist';

const { TextArea } = Input;

const YES_NO = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

const { Title, Text } = Typography;

type Props = {
  control: Control<PreLodgementChecklistFormValues>;
  error: FieldErrors<PreLodgementChecklistFormValues>;
};

export const FuturePlansStep: React.FC<Props> = ({ control, error }) => {
  const planningToBuyAnswer = useWatch({
    control,
    name: 'planningToBuyAnswer',
  });
  const propertySaleAnswer = useWatch({ control, name: 'propertySaleAnswer' });
  const repatriationAnswer = useWatch({ control, name: 'repatriationAnswer' });
  const taxResidencyStatus = useWatch({ control, name: 'taxResidencyStatus' });

  return (
    <div>
      <Title level={5} style={{ marginBottom: 4, marginTop: 0 }}>
        Future Plans
      </Title>
      <Text type="secondary">
        Tell us what's coming up so we can tailor our advice.
      </Text>
      <Divider style={{ margin: '12px 0 20px' }} />

      <Row gutter={[16, 16]}>
        {taxResidencyStatus === 'non_tax_resident' && (
          <>
            <Col xs={24}>
              <RadioGroupField
                label="Do you have plans to repatriate to Australia?"
                name="repatriationAnswer"
                control={control}
                options={YES_NO}
                error={error?.repatriationAnswer?.message}
                cols={{ sm: 2, md: 2, lg: 2 }}
              />
            </Col>
            {repatriationAnswer === true && (
              <Controller
                name="repatriationDate"
                control={control}
                render={({ field }) => {
                  const [month = '', year = ''] = (field.value || '').split(
                    '-',
                  );
                  const hasError = Boolean(error?.repatriationDate);
                  return (
                    <>
                      <Col
                        xs={24}
                        md={10}
                        style={{ display: 'flex', alignItems: 'center' }}
                      >
                        <FormLabel label="When do you expect to repatriate back to Australia?" />
                      </Col>
                      <Col xs={24} md={14}>
                        <Row gutter={12}>
                          <Col span={12}>
                            <div className="form-control column">
                              <FormLabel label="Month" />
                              <div className="input-containter">
                                <Select
                                  value={month || undefined}
                                  placeholder="Select month"
                                  options={MONTHS}
                                  onChange={(m) =>
                                    field.onChange(`${m}-${year}`)
                                  }
                                  style={{ width: '100%' }}
                                  status={hasError ? 'error' : undefined}
                                />
                              </div>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div className="form-control column">
                              <FormLabel label="Year" />
                              <div className="input-containter">
                                <Select
                                  value={year || undefined}
                                  placeholder="Select year"
                                  options={FUTURE_YEARS}
                                  onChange={(y) =>
                                    field.onChange(`${month}-${y}`)
                                  }
                                  style={{ width: '100%' }}
                                  status={hasError ? 'error' : undefined}
                                />
                              </div>
                            </div>
                          </Col>
                        </Row>
                        {error?.repatriationDate?.message && (
                          <ErrorText error={error.repatriationDate.message} />
                        )}
                      </Col>
                    </>
                  );
                }}
              />
            )}
          </>
        )}
        <Col xs={24}>
          <RadioGroupField
            label="Are you planning to buy any Australian property in the next 12 months?"
            name="planningToBuyAnswer"
            control={control}
            options={YES_NO}
            error={error?.planningToBuyAnswer?.message}
            cols={{ sm: 2, md: 2, lg: 2 }}
          />
          {planningToBuyAnswer === true && (
            <div style={{ marginTop: 12 }}>
              <Controller
                name="planningToBuyNotes"
                control={control}
                render={({ field }) => (
                  <TextArea
                    {...field}
                    placeholder="What kind of property, when, where — e.g. apartment in Sydney, house in Queensland..."
                    status={error?.planningToBuyNotes ? 'error' : undefined}
                    autoSize={{ minRows: 3, maxRows: 6 }}
                    styles={{ textarea: { lineHeight: 1.6 } }}
                  />
                )}
              />
              {error?.planningToBuyNotes?.message && (
                <ErrorText error={error.planningToBuyNotes.message} />
              )}
            </div>
          )}
        </Col>
        <Col xs={24}>
          <RadioGroupField
            label="Are you planning to sell any Australian property in the next 12 months?"
            name="propertySaleAnswer"
            control={control}
            options={YES_NO}
            error={error?.propertySaleAnswer?.message}
            cols={{ sm: 2, md: 2, lg: 2 }}
          />
          {propertySaleAnswer === true && (
            <div style={{ marginTop: 12 }}>
              <Controller
                name="propertySaleNotes"
                control={control}
                render={({ field }) => (
                  <TextArea
                    {...field}
                    placeholder="Which Australian property, expected timeline, reason for selling..."
                    status={error?.propertySaleNotes ? 'error' : undefined}
                    autoSize={{ minRows: 3, maxRows: 6 }}
                    styles={{ textarea: { lineHeight: 1.6 } }}
                  />
                )}
              />
              {error?.propertySaleNotes?.message && (
                <ErrorText error={error.propertySaleNotes.message} />
              )}
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};
